/**
 * Row provider identity — "which synced integration backs this row, if any?"
 *
 * ## Why this exists (banked learnings, 2026-06-26)
 *
 * Code that reasons about a row's PROVIDER (the sync-logs toggle, integration
 * affordances, sync status) kept hand-rolling
 * `row.variant instanceof SourceRow && key.startsWith(":~")`. That check is wrong
 * in two ways we verified the hard way (a `:~weather` ux-test that found "no
 * Show Sync Logs"):
 *
 *  1. **Data-preset rows are ALIASES, not SourceRows.** A wire `type:"data"` row
 *     whose `lookupKey` matches a registered data preset is rewritten by
 *     `dataBuilder` (`raw-sheet.ts`) into `{ role:"alias", template:"data" }`. So
 *     every preset-backed provider that renders as a chart (`:~garmin:stress`,
 *     `:~whoop:recovery`, the `:~dbg:data:*` demos) hydrates as an `AliasRow`,
 *     and the underlying `:~` source key then survives only as a `source`
 *     *attribute* on the nested `data-series` spec — never as a child
 *     `SourceRow.lookupKey`. A bare provider that is NOT a preset
 *     (`:~weather:{lid}`, `:~google:…`) stays a `SourceRow`. Same logical
 *     concept ("a synced provider row"), two different variants, so
 *     `instanceof SourceRow` answers the wrong question.
 *  2. **The `:~` lookupKey is retained on the row regardless of variant.** The
 *     descriptor role flips to `alias`, but `RowMetadataStore` keeps the row's
 *     original `lookupKey`. So `row.lookupKey` is the ONE field that identifies
 *     the provider uniformly across source rows and alias-wrapped rows. Resolve
 *     provider identity from the row's own retained key — not from `instanceof`,
 *     not from a source descendant (which may not exist until an alias expands).
 *
 * ## What this does NOT decide
 *
 * Whether a provider actually *has* sync logs (or any other capability) is an
 * APP concern, not an SDK fact — only `weather` has a registered
 * `ColumnLogsRendererPlugin.Config` today (a host's plugin registration). This module
 * resolves provider IDENTITY; the app layers capability on top
 * (`hasSyncLogConfig(RowProvider.familyOf(row.lookupKey))`, …). Keep
 * provider→capability maps out of here.
 *
 * ## Canonical-key shape
 *
 * `[owner]:~<provider>:<rest>` or `:~<provider>:<rest>` (owner + rest optional),
 * mirroring `isCanonicalKey` (`backends/canonical-backend.ts`). `<provider>` is
 * the integration family (`weather`, `garmin`, `whoop`, `google`, …). Two
 * families are INTERNAL app namespaces, not external integrations: `lona`
 * (`:~lona:tasks` builtins) and `dbg` (`:~dbg:data:*` synthetic demos). External
 * checks exclude those — a synthetic sine-wave demo must never be offered sync
 * affordances.
 *
 * Adopt this from: the envelope `row:lookup-key` spec gate (`row-envelopes.ts`,
 * currently `applies: row instanceof SourceRow` — misses alias-wrapped
 * providers) and a host's sync-logs gate (its plugin registration).
 */

import type { Option } from "../core/option.ts";
import type { ModelRow } from "./model-row.ts";
import { SourceRow } from "./variants/source-row.ts";
import { elementAt } from "../core/checked-index.ts";
import { frozenSet } from "../core/frozen-collections.ts";

/**
 * Canonical row keys by provider family: decomposing a key and telling synced
 * integrations from app namespaces.
 */
export namespace RowProvider {
  /** Decomposed canonical lookup key. */
  export interface KeyParts {
    /** `[weather@lona.so]:~…` → `"weather@lona.so"`; `null` when unscoped. */
    readonly owner: Option<string>;
    /** `:~weather:…` → `"weather"` — the integration family segment. */
    readonly provider: string;
    /** Everything after `:~<provider>:` (`":~weather:us-sf"` → `"us-sf"`). */
    readonly rest: Option<string>;
  }

  /**
   * Provider families that are INTERNAL app namespaces rather than external
   * synced integrations. Canonical keys in these families are real rows, but
   * NOT integration sources — exclude them from external-provider affordances
   * (sync logs, "reconnect", …). See module doc.
   */
  export const INTERNAL_FAMILIES: ReadonlySet<string> = frozenSet([
    "lona", // `:~lona:tasks`, `:~lona:weather` — app builtins
    "dbg", //  `:~dbg:data:sine` — synthetic demo data
  ]);

  // Mirrors `isCanonicalKey` (`backends/canonical-backend.ts`), but captures the
  // owner / provider / rest segments rather than just testing the shape.
  const KEY_RE = /^(?:\[([^\]]+)\])?:~([^:]+)(?::(.*))?$/;

  /**
   * Parse `[owner]:~<provider>:<rest>` into its parts, or `null` when `key` is
   * absent or not canonical (no `:~`).
   */
  export function parseKey(key: Option<string>): Option<KeyParts> {
    if (key == null) return null;
    const m = KEY_RE.exec(key);
    if (m == null) return null;
    return {
      owner: m[1] ?? null,
      provider: elementAt(m, 2, "RowProvider.parseKey: provider group"),
      rest: m[3] ?? null,
    };
  }

  /**
   * The integration family of a key (`":~garmin:stress"` → `"garmin"`), or
   * `null` when not canonical. Returns internal families (`lona`/`dbg`) too —
   * use {@link isExternal} to filter to real integrations.
   */
  export function familyOf(key: Option<string>): Option<string> {
    return parseKey(key)?.provider ?? null;
  }

  /**
   * Whether `key` names an EXTERNAL synced integration: canonical, and not an
   * internal `lona`/`dbg` namespace. This is the provider-identity predicate to
   * gate sync affordances on — NOT `key.startsWith(":~")` (too broad: matches
   * builtins + demos) and NOT `instanceof SourceRow` (too narrow: misses
   * alias-wrapped preset providers).
   */
  export function isExternal(key: Option<string>): boolean {
    const provider = familyOf(key);
    return provider != null && !INTERNAL_FAMILIES.has(provider);
  }

  /**
   * Whether `key` is owned by someone other than `currentUser`: it carries an
   * `[owner]` prefix (`[weather@lona.so]:~…`) whose owner differs from the
   * current user. Unscoped keys (own rows) are never foreign. Use this to gate
   * identity edits (e.g. changing the lookup key) on rows we don't own.
   */
  export function isForeignOwned(
    key: Option<string>,
    currentUser: Option<string>,
  ): boolean {
    const owner = parseKey(key)?.owner ?? null;
    return owner != null && owner !== currentUser;
  }

  /**
   * Depth-first search for the first `SourceRow` at or beneath `row`: the row
   * itself when it is a `SourceRow`, else its first source descendant. `null`
   * for a not-yet-expanded alias or a pure layout/inert subtree.
   *
   * Provider IDENTITY does NOT depend on this (read `row.lookupKey` /
   * {@link familyOf}); use it only for cell-write / facade access that needs the
   * concrete source row.
   */
  export function firstSource(row: ModelRow): Option<SourceRow> {
    if (row instanceof SourceRow) return row;
    for (const child of row.children) {
      const found = firstSource(child);
      if (found) return found;
    }
    return null;
  }
}
